import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { AccommodationCombined, AccommodationCombinedType } from '../Accommodations/Accommodation.types';
import { PriceComparisonFragment, TravelPlacePricingAndBookingDetailsFragment } from '@graphql/generated';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';

// Number of decimal places to round ratios to
const DECIMAL_PLACES = 1;
const safeRatio = (numerator: number, denominator: number): number => {
  if (denominator === 0) return 0;

  const ratio = numerator / denominator;
  const factor = Math.pow(10, DECIMAL_PLACES);

  return Math.round(ratio * factor) / factor;
};

/**
 * Represents counts and statistics for accommodation hotels.
 */
export interface AccommodationsHotelsCounts {
  /** Number of hotels added in accommodations page */
  countAllStays: number;
  /** Number of partner hotels */
  countPartnerHotels: number;
  /** countPartnerHotels / countAllStays */
  ratioPartnerHotels: number;
  /** Number of room blocks */
  countRoomBlocks: number;
  /** Couple selected custom link checkbox */
  countCustomLinks: number;
  /** Number of non-partner hotels */
  countNonPartnerHotels: number;
  /** Number of partner hotels where we have expedia prices */
  countPartnerHotelsWPriceCompare: number;
  /** countPartnerHotelsWPriceCompare / countAllStays */
  ratioPriceCompareAvailable: number;
  /** Number of partner hotels where we don't have expedia prices */
  countPartnerHotelsWOPriceCompare: number;
  /** Number of partner hotels where our price is lowest compared to competitors */
  countPartnerHotelsWBetterPrice: number;
  /** countPartnerHotelsWBetterPrice / countAllStays */
  ratioBetterPrice: number;
  /** Number of partner hotels where our price is not lowest compared to competitors */
  countPartnerHotelsWWorsePrice: number;
  /** Number of partner hotels where hotels are already sold out */
  countUnavailableStays: number;
}

/**
 * Returns various counts related to accommodations
 *
 * @param {AccommodationCombined[]} accommodations - List of accommodations.
 * @returns {{
 *   accommodationsHotelsCounts: AccommodationsHotelsCounts;
 *   accommodationsHotelsCountsLoading: boolean;
 * }}
 */

export const useAccommodationsHotelsCounts = (
  accommodations: AccommodationCombined[],
  priceDataList: TravelPlacePricingAndBookingDetailsFragment[],
  travelPlaces: PriceComparisonFragment[],
) => {
  const accommodationsHotelsCounts: AccommodationsHotelsCounts = {
    countAllStays: 0,
    countPartnerHotels: 0,
    ratioPartnerHotels: 0,
    countRoomBlocks: 0,
    countCustomLinks: 0,
    countNonPartnerHotels: 0,
    countPartnerHotelsWPriceCompare: 0,
    ratioPriceCompareAvailable: 0,
    countPartnerHotelsWOPriceCompare: 0,
    countPartnerHotelsWBetterPrice: 0,
    ratioBetterPrice: 0,
    countPartnerHotelsWWorsePrice: 0,
    countUnavailableStays: 0,
  };

  const [showNewPriceComparisonUI, showNewPriceComparisonUILoading] = useIsFeatureEnabled('enableAccommodationsPriceComparisonV2');

  const nonRoomBlockAccommodations: AccommodationCombined[] = [];
  const joyPlaceIdSet = new Set<string>();
  const accommodationPlaceIds: string[] = [];

  const countAllStays = accommodations.length;
  accommodationsHotelsCounts.countAllStays = countAllStays;
  accommodations.forEach(acc => {
    if (acc.type === AccommodationCombinedType.Roomblock) {
      accommodationsHotelsCounts.countRoomBlocks++;
      return;
    }
    if (acc.isCustomLinkEnabled) {
      accommodationsHotelsCounts.countCustomLinks++;
      return;
    }
    if (acc.joyPlaceId) {
      accommodationsHotelsCounts.countPartnerHotels++;
      nonRoomBlockAccommodations.push(acc);
      joyPlaceIdSet.add(acc.joyPlaceId);
      accommodationPlaceIds.push(acc.id);
    } else {
      accommodationsHotelsCounts.countNonPartnerHotels++;
    }
  });

  const joyPlaceToAccommodationIdMap = new Map(nonRoomBlockAccommodations.map(acc => [acc.joyPlaceId, acc.id]));
  const accommodationIdToPriceDataMap = new Map(priceDataList.map(pd => [pd.accommodationPlace?.id, pd]));
  if (showNewPriceComparisonUI) {
    travelPlaces.forEach(travelPlace => {
      const joyPlaceId = travelPlace.joyPlaceId;
      const accommodationId = joyPlaceToAccommodationIdMap.get(joyPlaceId);
      const priceData = accommodationId ? accommodationIdToPriceDataMap.get(accommodationId) : undefined;

      const joyBasePrice = priceData?.pricingInfo?.nightlyBaseRate?.valueInMinorUnits;
      const competitorPrices = travelPlace.providers?.map(p => p.nightlyBaseRate?.valueInMinorUnits).filter((p): p is number => p != null) ?? [];

      if (joyBasePrice && competitorPrices.length > 0) {
        accommodationsHotelsCounts.countPartnerHotelsWPriceCompare++;
        const lowestCompetitorPrice = Math.min(...competitorPrices);
        if (joyBasePrice < lowestCompetitorPrice) {
          accommodationsHotelsCounts.countPartnerHotelsWBetterPrice++;
        } else {
          accommodationsHotelsCounts.countPartnerHotelsWWorsePrice++;
        }
      } else {
        accommodationsHotelsCounts.countPartnerHotelsWOPriceCompare++;
      }
      if (extractHotelRedirects(priceData?.redirectMechanisms)?.hotelSearchPageUrl?.url) {
        accommodationsHotelsCounts.countUnavailableStays++;
      }
    });
    accommodationsHotelsCounts.countPartnerHotelsWOPriceCompare = accommodationsHotelsCounts.countPartnerHotels - accommodationsHotelsCounts.countPartnerHotelsWPriceCompare;
  } else {
    priceDataList.forEach(priceData => {
      const joyBasePrice = priceData.pricingInfo?.nightlyBaseRate?.valueInMinorUnits;
      const strikePrice = priceData.pricingInfo?.strikeoutNightlyBaseRate?.valueInMinorUnits;
      if (joyBasePrice && strikePrice) {
        accommodationsHotelsCounts.countPartnerHotelsWPriceCompare++;
        if (joyBasePrice < strikePrice) {
          accommodationsHotelsCounts.countPartnerHotelsWBetterPrice++;
        } else {
          accommodationsHotelsCounts.countPartnerHotelsWWorsePrice++;
        }
      } else {
        accommodationsHotelsCounts.countPartnerHotelsWOPriceCompare++;
      }
      if (extractHotelRedirects(priceData.redirectMechanisms)?.hotelSearchPageUrl?.url) {
        accommodationsHotelsCounts.countUnavailableStays++;
      }
    });
  }
  accommodationsHotelsCounts.ratioPartnerHotels = safeRatio(accommodationsHotelsCounts.countPartnerHotels, countAllStays);
  accommodationsHotelsCounts.ratioPriceCompareAvailable = safeRatio(accommodationsHotelsCounts.countPartnerHotelsWPriceCompare, countAllStays);
  accommodationsHotelsCounts.ratioBetterPrice = safeRatio(accommodationsHotelsCounts.countPartnerHotelsWBetterPrice, countAllStays);

  return { accommodationsHotelsCounts, accommodationsHotelsCountsLoading: showNewPriceComparisonUILoading };
};
